import { Typography } from '@cannect/new-components/atoms'
import { LuArrowRightCircle } from 'react-icons/lu'

const BLOG_POST_BASE_URL = 'https://blog.cannect.life/post'
const WORDS_PER_MINUTE = 200
const MAX_POSTS = 3

/** Shape efetivo de `getCannalizePostsPaginated`; só os campos usados aqui. */
type TBlogPost = {
  ID: number
  post_title: string
  post_name: string
  post_content?: string
  image_urls?: string[]
  category?: string[]
}

type TBlogHighlightsProps = {
  posts?: TBlogPost[]
}

const stripHtml = (html?: string) => {
  if (!html) return ''
  const container = document.createElement('div')
  container.innerHTML = html
  return container.textContent || container.innerText || ''
}

/**
 * O design mostra "6 min de leitura" por card, mas o post não traz esse dado — é calculado a
 * partir da contagem de palavras do conteúdo, no padrão de 200 palavras por minuto.
 */
const getReadingTime = (content?: string) => {
  const words = stripHtml(content).trim().split(/\s+/).filter(Boolean).length
  if (!words) return null
  return Math.max(1, Math.round(words / WORDS_PER_MINUTE))
}

/**
 * Seção "Conteúdo e educação" da home (Figma node 4161:23574).
 *
 * Três cards de post reais do blog. O protótipo traz conteúdo fictício; aqui os dados vêm de
 * `useGetCannectPosts`, recebidos por prop para não duplicar a query da página.
 *
 * Contraste: o design usa `secondary-500` na categoria (2,67:1), `neutral-500` no tempo de leitura
 * (3,53:1) e `primary-700` no "Leia mais" (3,59:1) — os três reprovam WCAG AA em texto. Aqui foram
 * escurecidos um degrau para `secondary-700` (4,58:1), `neutral-700` (7,33:1) e `marketplace-800`
 * (6,58:1), preservando a hierarquia visual.
 */
export const BlogHighlights = ({ posts }: TBlogHighlightsProps) => {
  const visiblePosts = posts?.slice(0, MAX_POSTS) ?? []

  if (!visiblePosts.length) return null

  return (
    <section aria-labelledby="blog-highlights-heading" className="w-full bg-neutral-50">
      <div className="mx-auto flex w-full max-w-[1230px] flex-col gap-8 px-8 py-16">
        {/* Cabeçalho centralizado com H2 de 36px (node 4333:1583) — não os 28px das demais seções.
            Mesma escala do "Jornada segura e confiável". */}
        <div className="flex flex-col items-center gap-4 text-center">
          <p className="m-0 text-base leading-[1.6] text-neutral-800">Conteúdo e educação</p>

          <Typography.Heading
            id="blog-highlights-heading"
            level={2}
            weight="regular"
            className="m-0 text-[36px] leading-[49.28px] tracking-[-0.448px] text-neutral-800">
            {/* `span`, não `em`: o itálico é assinatura de marca, não ênfase semântica. */}
            Informação para decidir com mais <span className="italic">segurança.</span>
          </Typography.Heading>
        </div>

        <ul className="m-0 grid list-none grid-cols-1 gap-6 p-0 md:grid-cols-2 lg:grid-cols-3">
          {visiblePosts.map((post) => {
            const image = post.image_urls?.[0]
            const category = post.category?.[0]
            const readingTime = getReadingTime(post.post_content)

            return (
              <li key={post.ID} className="flex flex-col rounded-[16px] bg-white px-4 pb-6 pt-4">
                {image && (
                  <img
                    src={image}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="h-[200px] w-full rounded-[8px] object-cover"
                  />
                )}

                <div className="mt-4 flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-4">
                    {category && (
                      <span className="text-xs font-semibold leading-[1.4] text-secondary-700">{category}</span>
                    )}
                    {readingTime && (
                      <span className="shrink-0 text-xs leading-[1.4] text-neutral-700">
                        {readingTime} min de leitura
                      </span>
                    )}
                  </div>

                  <h3 className="m-0 text-lg font-semibold leading-[1.4] text-neutral-800">{post.post_title}</h3>
                </div>

                <a
                  href={`${BLOG_POST_BASE_URL}/${post.post_name}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Leia mais: ${post.post_title}`}
                  className="mt-4 inline-flex items-center gap-[10px] self-start text-sm leading-[1.4] text-marketplace-800 no-underline transition-opacity hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-marketplace-800 focus-visible:ring-offset-2">
                  Leia mais
                  <LuArrowRightCircle className="h-4 w-4 shrink-0" aria-hidden />
                </a>
              </li>
            )
          })}
        </ul>
      </div>
    </section>
  )
}
